import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import { Box, Button, Card, CardContent, IconButton, Input, Tooltip, Typography, Avatar, Grid, useTheme, useMediaQuery, Badge, CircularProgress, Container, Stack, Fade, Zoom } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { teamManagementApi } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST } from "../../utils/constants";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EditOutlined, ArrowBack as ArrowBackIcon, Edit as EditIcon, SportsSoccer as SoccerIcon } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { formatPrice } from "../../utils/formatNumber";
import { Helmet } from "react-helmet-async";
import ViewTeamManagement from "./View";
import { 
  updateSearchParams, 
  updatePageParams, 
  updateFilterParams 
} from "../../utils/urlParams";
import BackButton from "../../components/Buttons/BackButton";
import "../../styles/PlayerList.css";

export default function PlayerList() {
  const { id , seasonId} = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [LeagueDetails, setLeagueDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Position mapping for displaying position names
  const positionNames = {
    24: "Goalkeeper",
    25: "Defender",
    26: "Midfielder",
    27: "Forward"
  };

  const location = useLocation();
  const { leagueData } = location.state || {};

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      teamId: id,
      seasonId: seasonId,
    };
    try {
      setIsLoading(true);
      const res = await teamManagementApi.getAll(body);
      if (res.status === 200) {
        setData(res?.data?.data?.result);
        setLeagueDetails(res?.data?.data?.league);
        // Set total records and calculate total pages
        const totalRecordsCount = res?.data?.data?.totalRecords || 0;
        setTotalRecords(totalRecordsCount);
        const calculatedTotalPages = Math.ceil(totalRecordsCount / pageSize);
        setTotalPages(calculatedTotalPages);

        // Debug logging
        console.log('API Response:', {
          totalRecords: totalRecordsCount,
          pageSize: pageSize,
          calculatedTotalPages: calculatedTotalPages,
          currentPage: currentPage,
          dataLength: res?.data?.data?.result?.length
        });
      }
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query !== searchItem) {
        setCurrentPage(1); // Reset to first page when searching
        // Update URL parameter after debounce
        updateSearchParams(searchParams, setSearchParams, query, 1);
        fetchUsers(query);
      }
    }, 400),
    [currentPage, pageSize, status, searchItem, searchParams, setSearchParams]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    
    // Don't update URL immediately - let debouncedSearch handle it
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    // Ensure page is within valid range
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Update URL parameter to maintain page state
      updatePageParams(searchParams, setSearchParams, page);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Initialize state from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const searchFromUrl = searchParams.get('search');
    const statusFromUrl = searchParams.get('status');
    const pageSizeFromUrl = searchParams.get('pageSize');
    
    console.log('PlayerList - Initializing from URL params:', {
      pageFromUrl,
      searchFromUrl,
      statusFromUrl,
      pageSizeFromUrl
    });
    
    // Update state
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (searchFromUrl) {
      setSearchItem(searchFromUrl);
    }
    
    if (statusFromUrl) {
      const statusArray = statusFromUrl.split(',').map(s => parseInt(s)).filter(s => !isNaN(s));
      setStatus(statusArray);
    }
    
    if (pageSizeFromUrl && !isNaN(pageSizeFromUrl) && parseInt(pageSizeFromUrl) > 0) {
      setPageSize(parseInt(pageSizeFromUrl));
    }
    
    // Use setTimeout to ensure all state updates are processed
    setTimeout(() => {
      setIsInitialized(true);
    }, 50);
  }, [searchParams]);

  useEffect(() => {
    // Only fetch data after initialization is complete
    if (!isInitialized) return;
    
    console.log('PlayerList - Fetching data with params:', {
      searchItem,
      currentPage,
      pageSize,
      status
    });
    
    fetchUsers(searchItem);
  }, [isInitialized, currentPage, pageSize, status, refresh, id]);

  // Reset current page if it exceeds total pages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "80px",
      center: true,
    },
    {
      name: "Player Name",
      selector: (row) => row.fullName,
      sortable: true,
    },
    {
      name: "Team Name",
      selector: (row) => row.teamName || "-",
      sortable: true,
    },
    {
      name: "Position",
      selector: (row) => positionNames[row.positionId] || `Position ${row.positionId}`,
      sortable: true,
      width: "120px",
    },
    {
      name: "Image",
      width: "100px",
      cell: (row) => (
        <Avatar
          src={row.imagePath}
          alt={row.displayName}
          onError={(e) => {
            e.target.src = "/assets/images/bg.png"
          }}
          className="player-avatar"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Price",
      selector: (row) => row.price ? row.price.toFixed(2) : "-",
      sortable: true,
      width: "120px",
    },
    {
      name: "Created At",
      selector: (row) => capitalize(fDate(row.createdAt)),
      sortable: true,
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View">
            <IconButton
              onClick={() => handleView(row._id)}
              color="primary"
              className="action-btn view-btn"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => handleEdit(row._id, id)}
              color="primary"
              className="action-btn edit-btn"
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const handleView = (id) => {
    setModalShow(true);
    setViewId(id);
  };

  const handleEdit = (Playerid, rowid) => {
    navigate(`/team-management/edit/${Playerid}/${rowid}`, {
      state: {
        leagueData: leagueData,
      }
    });
  };

  // Extract team name from API response data
  const getTeamName = () => {
    if (data.length > 0) {
      return data[0].team.teamName || "Unknown Team";
    }
    return "Loading...";
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Helmet>
        <title>Player List - Squad Duel Admin</title>
      </Helmet>

      {/* Enhanced Header Card */}
      <Card elevation={8} className="pl-header-card">
        <Box className="pl-header-card-bg" />
        <CardContent className="pl-header-card-content">

          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              className="pl-back-button"
            >
              Back
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
            {/* Left Section - Back Button and Title */}

            <Stack direction="row" alignItems="center" spacing={2} className="pl-title-section">
              <Box className="pl-title-icon-container">
                <PeopleIcon className="pl-title-icon" />
              </Box>
              <Typography variant="h4" className="pl-title-text">
                Player List
              </Typography>
            </Stack>

            {/* Right Section - Two Separate Boxes for League and Team */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              className="pl-info-boxes-container"
            >
              <Box className="pl-league-box">
                <Typography variant="body2" className="pl-league-label">
                  Country
                </Typography>
                <Typography variant="h6" className="pl-league-name">
                  {LeagueDetails?.country || "Unknown League"}
                </Typography>
              </Box>
              {/* League Name Box */}
              <Box className="pl-league-box">
                <Typography variant="body2" className="pl-league-label">
                  League
                </Typography>
                <Typography variant="h6" className="pl-league-name">
                  {LeagueDetails?.name || "Unknown League"}
                </Typography>
              </Box>

              {/* Team Name Box */}
              <Box className="pl-team-box">
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mb: 1 }}>
                  <Typography variant="body2" className="pl-team-label">
                    Team
                  </Typography>
                </Stack>
                <Typography variant="h5" className="pl-team-name">
                  {getTeamName()}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Enhanced Search Card */}
      <Card elevation={4} className="pl-search-card">
        <CardContent className="pl-search-card-content">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            className="pl-search-container"
          >
            {/* Left: Search */}
            <Box className="pl-search-input-container">
              <Input
                placeholder="Search..."
                value={searchItem}
                onChange={handleSearchChange}
                className="pl-search-input"
                disableUnderline
                startAdornment={<SearchIcon className="pl-search-icon" />}
                endAdornment={
                  searchItem && (
                    <IconButton
                      onClick={() => {
                        setSearchItem("");
                        setCurrentPage(1);
                        
                        // Update URL parameter immediately for clear action
                        updateSearchParams(searchParams, setSearchParams, "", 1);
                        
                        fetchUsers("");
                      }}
                      size="small"
                      edge="end"
                    >
                      <CloseIcon />
                    </IconButton>
                  )
                }
              />
            </Box>


            {/* Right: Page Info */}
            <Typography variant="body2" className="pl-page-info">
              Page {currentPage} of {totalPages} ({totalRecords} total players)
            </Typography>
          </Stack>
        </CardContent>
      </Card>


      {/* Enhanced Loading State */}
      {isLoading && (
        <Box className="pl-loading-container">
          <CircularProgress
            size={60}
            thickness={4}
            className="pl-loading-spinner"
          />
          <Typography variant="h6" className="pl-loading-title">
            Loading players...
          </Typography>
          <Typography variant="body2" className="pl-loading-subtitle">
            Please wait while we fetch the latest data
          </Typography>
        </Box>
      )}

      {/* Enhanced Players Grid */}
      {!isLoading && data.length > 0 && (
        <>
          <Grid container spacing={3} className="pl-player-grid">
            {data.map((player, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={`${currentPage}-${player._id}-${index}`}>
                <Zoom in={!isLoading} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card elevation={6} className="pl-player-card">
                    <CardContent className="pl-player-card-content">
                      {/* Enhanced Player Avatar with Badge */}
                      <Box className="pl-player-avatar-container">
                        <Badge
                          badgeContent={(currentPage - 1) * pageSize + index + 1}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              backgroundColor: '#00a1dd',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              minWidth: 24,
                              height: 24
                            }
                          }}
                        >
                          <Avatar
                            src={player.imagePath}
                            alt={player.displayName}
                            onError={(e) => {
                              e.target.src = "/assets/images/bg.png";
                            }}
                            className="pl-player-avatar-large"
                          />
                        </Badge>
                      </Box>

                      {/* Player Name */}
                      <Typography variant="h6" className="pl-player-name">
                        {player.fullName}
                      </Typography>

                      {/* Player Position */}
                      <Box className="pl-player-position-container">
                        <Typography variant="body2" className="pl-position-label">
                          Position
                        </Typography>
                        <Typography variant="body1" className="pl-position-value">
                          {positionNames[player.positionId] || `Position ${player.positionId}`}
                        </Typography>
                      </Box>

                      {/* Player Price */}
                      <Box className="pl-player-price-container">
                        <Typography variant="body2" className="pl-price-label">
                          Price
                        </Typography>
                        <Typography variant="h6" className="pl-price-value">
                          ${player.price ? player.price.toFixed(2) : 0}m
                        </Typography>
                      </Box>

                      {/* Enhanced Action Buttons */}
                      <Stack direction="row" spacing={1} justifyContent="center" className="pl-player-actions">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleView(player._id)}
                          startIcon={<VisibilityIcon />}
                          className="pl-view-button"
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(player._id, id)}
                          startIcon={<EditIcon />}
                          className="pl-edit-button"
                        >
                          Edit
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          {/* Enhanced Pagination */}
          <Card elevation={4} className="pl-pagination-card">
            <CardContent className="pl-pagination-card-content">
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={3}
              >
                {/* Left Side - Info and Page Size */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" className="pl-pagination-info">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, (currentPage - 1) * pageSize + data.length)} of {totalRecords} players
                  </Typography>

                  <Stack direction="row" spacing={1} className="pl-page-size-buttons">
                    {[10, 20, 50].map((size) => (
                      <Button
                        key={size}
                        size="small"
                        variant={pageSize === size ? "contained" : "outlined"}
                        onClick={() => handlePageSizeChange(size)}
                        className={`pl-page-size-button ${pageSize === size ? 'active' : 'inactive'}`}
                      >
                        {size}
                      </Button>
                    ))}
                  </Stack>
                </Stack>

                {/* Right Side - Pagination Info and Buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>

                  <Stack direction="row" spacing={1} className="pl-pagination-buttons">
                    {[
                      { label: 'First', action: () => handlePageChange(1), disabled: currentPage === 1 },
                      { label: 'Previous', action: () => handlePageChange(currentPage - 1), disabled: currentPage === 1 },
                      { label: 'Next', action: () => handlePageChange(currentPage + 1), disabled: currentPage >= totalPages },
                      { label: 'Last', action: () => handlePageChange(totalPages), disabled: currentPage >= totalPages }
                    ].map((btn) => (
                      <Button
                        key={btn.label}
                        size="small"
                        variant="outlined"
                        onClick={btn.action}
                        disabled={btn.disabled}
                        className="pl-pagination-button"
                      >
                        {btn.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Enhanced No Data State */}
      {!isLoading && data.length === 0 && (
        <Fade in={true}>
          <Box className="pl-no-data-container">
            <Box className="pl-no-data-icon-container">
              <PeopleIcon className="pl-no-data-icon" />
            </Box>
            <Typography variant="h5" className="pl-no-data-title">
              No players found
            </Typography>
            <Typography variant="body1" className="pl-no-data-subtitle">
              There are no players in this team yet. Players will appear here once they are added to the team.
            </Typography>
          </Box>
        </Fade>
      )}

      {/* View Modal */}
      <ViewTeamManagement
        id={viewId}
        show={modalShow}
        onHide={() => {
          console.log('Modal closing');
          setModalShow(false);
        }}
        leagueData={leagueData}
        teamID={id}
      />
    </Container>
  );
}
